import {
  getAnalytics,
  logEvent,
  setAnalyticsCollectionEnabled,
  type Analytics,
} from 'firebase/analytics'
import { app, measurementId } from './firebase'

/**
 * Google Analytics, chargé seulement après consentement.
 *
 * Firebase Analytics dépose ses cookies et commence à mesurer dès `getAnalytics()`.
 * L'initialiser au démarrage puis « désactiver » ensuite serait déjà trop tard : la
 * CNIL exige un consentement *préalable*. D'où l'initialisation paresseuse.
 */

const STORAGE_KEY = 'analytics-consent'

type Consent = 'granted' | 'denied'

let instance: Analytics | null = null

export const isAnalyticsAvailable = Boolean(app && measurementId)

/** `null` tant que l'utilisateur n'a pas répondu. */
export function storedConsent(): Consent | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    return null // navigation privée, stockage bloqué : on ne mesure pas.
  }
}

export function setConsent(granted: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied')
  } catch { /* on retombera sur « pas de consentement » */ }

  if (granted) {
    start()
  } else if (instance) {
    setAnalyticsCollectionEnabled(instance, false)
    instance = null
  }
}

/** À appeler au démarrage : ne fait rien tant que le consentement n'est pas donné. */
export function startIfConsented(): void {
  if (storedConsent() === 'granted') start()
}

function start(): void {
  if (instance || !app || !measurementId) return
  instance = getAnalytics(app)
  setAnalyticsCollectionEnabled(instance, true)
}

/** Silencieux si l'utilisateur a refusé — pas d'erreur, pas de file d'attente. */
export function track(name: string, params: Record<string, unknown> = {}): void {
  if (!instance) return
  logEvent(instance, name, params)
}
